import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { LogOut, ClipboardList, Plus, FileSpreadsheet, Home as HomeIcon, Users, Crown, Clock, ArrowLeft, ClipboardCheck, KeyRound, MessageSquare, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import CraneIcon from "@/components/CraneIcon";
import { APP_VERSION } from "@/version";
import { useCallback, useEffect, useRef, useState } from "react";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "sadmin";
  const isSadmin = user?.role === "sadmin";
  const showBack = location.pathname !== "/";

  const canSeeCrane = isSadmin || user?.can_view_crane_calendar || user?.can_manage_crane_calendar;

  const navItems = [
    { to: "/", label: "Inicio", icon: HomeIcon, testId: "nav-home" },
    { to: "/new", label: "Nuevo", icon: Plus, testId: "nav-new" },
    { to: "/orders", label: "Agenda", icon: CalendarIcon, testId: "nav-orders" },
    { to: "/logs", label: "Partes", icon: ClipboardList, testId: "nav-logs" },
    { to: "/pjc/mine", label: "PJC", icon: ClipboardCheck, testId: "nav-pjc" },
    ...(canSeeCrane ? [
      { to: "/crane", label: "Grúa", icon: CraneIcon, testId: "nav-crane" },
    ] : []),
    ...(isAdmin ? [
      { to: "/export", label: "Exportar", icon: FileSpreadsheet, testId: "nav-export" },
      { to: "/admin/pjc", label: "PJC eq.", icon: ClipboardCheck, testId: "nav-admin-pjc" },
      { to: "/admin/sugerencias", label: "Buzón", icon: MessageSquare, testId: "nav-sugerencias" },
    ] : []),
    ...(isSadmin ? [
      { to: "/users", label: "Usuarios", icon: Users, testId: "nav-users" },
      { to: "/sadmin/stats", label: "Stats", icon: Crown, testId: "nav-sadmin" },
      { to: "/sadmin/fichajes", label: "Fichajes", icon: Clock, testId: "nav-sadmin-fichajes" },
    ] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F7FAFD] via-white to-[#F7FAFD]">
      {/* Top bar */}
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {showBack && (
              <button
                type="button"
                data-testid="back-btn"
                aria-label="Volver"
                onClick={() => {
                  if (window.history.length > 1) navigate(-1);
                  else navigate("/");
                }}
                className="h-10 w-10 rounded-full border border-slate-200 text-slate-600
                           hover:text-[#F5A623] hover:border-[#F5A623]/60 hover:bg-orange-50/40
                           transition-colors inline-flex items-center justify-center"
              >
                <ArrowLeft size={18} strokeWidth={2.2} />
              </button>
            )}
            <Link to="/" data-testid="brand-link">
              <Logo variant="dark" size="md" />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                {isAdmin ? "Administrador" : "Operario"}
              </div>
              <div className="text-sm font-semibold truncate max-w-[200px]" data-testid="user-name">
                {user?.name}
              </div>
            </div>
            <Link
              to="/change-password"
              data-testid="change-password-link"
              title="Cambiar mi contraseña"
              className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600
                         hover:text-[#0E78C2] hover:border-[#0E78C2]/60 hover:bg-blue-50/40
                         transition-colors text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <KeyRound size={14} /> <span className="hidden sm:inline">Clave</span>
            </Link>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="h-10 px-3 rounded-lg border border-slate-200 text-slate-600
                         hover:text-[#F5A623] hover:border-[#F5A623]/60 hover:bg-orange-50/40
                         transition-colors text-xs font-semibold inline-flex items-center gap-1.5"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-28">
        <Outlet />
        <div
          data-testid="app-version-footer"
          className="mt-10 flex items-center justify-center gap-1.5 text-[10px] font-mono text-slate-400 select-none"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F5A623] animate-pulse" />
          <span className="uppercase tracking-[0.2em]">beta</span>
          <span className="text-slate-300">·</span>
          <span>v{APP_VERSION}</span>
        </div>
      </main>

      {/* Bottom nav (mobile-first) — horizontal scroll with arrow controls */}
      <BottomNav navItems={navItems} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Horizontally scrollable bottom nav. Arrows appear on the sides and scroll
// the strip left/right. Icons/labels keep enough breathing room on mobile.
function BottomNav({ navItems }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    // Center on the active item on mount
    const active = el.querySelector("a[aria-current='page']");
    if (active) {
      const offset = active.offsetLeft - el.clientWidth / 2 + active.clientWidth / 2;
      el.scrollTo({ left: Math.max(0, offset), behavior: "instant" });
      setTimeout(updateArrows, 30);
    }
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows, navItems.length]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: "smooth" });
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 z-40 shadow-[0_-8px_30px_-15px_rgba(11,42,74,0.15)]"
      data-testid="bottom-nav"
    >
      <div className="relative max-w-3xl mx-auto">
        {/* Left arrow */}
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Deslizar navegación a la izquierda"
          data-testid="bottom-nav-left"
          className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-8 flex items-center justify-center bg-gradient-to-r from-white via-white to-transparent transition-opacity ${canLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronLeft size={18} className="text-[#0B2A4A]" strokeWidth={2.4} />
        </button>

        {/* Scrollable strip */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scroll-smooth px-8"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`nav[data-testid="bottom-nav"] div::-webkit-scrollbar{display:none}`}</style>
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `shrink-0 min-w-[72px] relative flex flex-col items-center gap-1 px-2 py-3 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                  isActive ? "text-[#F5A623]" : "text-slate-500 hover:text-[#0B2A4A]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[3px] rounded-b-full bg-gradient-to-r from-[#FF8A1F] to-[#F5A623]" />
                  )}
                  <Icon size={20} strokeWidth={2.2} />
                  <span className="whitespace-nowrap">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Right arrow */}
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Deslizar navegación a la derecha"
          data-testid="bottom-nav-right"
          className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-8 flex items-center justify-center bg-gradient-to-l from-white via-white to-transparent transition-opacity ${canRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <ChevronRight size={18} className="text-[#0B2A4A]" strokeWidth={2.4} />
        </button>
      </div>
    </nav>
  );
}
